import { inject } from '@angular/core';
import type {
  CanActivateFn,
  CanMatchFn,
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
  UrlSegment,
  UrlTree,
  Route,
} from '@angular/router';
import { Router } from '@angular/router';
import { AuthService } from '../../services/auth.service';
import { AuthorizationService } from '../../services/AuthorizationService.service';
import { UserContextService } from '../context/user.context';
import { TimersService } from '../../services/timers.service';

export const PUBLIC_ROUTE_PREFIXES = ['/portal-ingreso-candidatos', '/obtener-documento'];

export const authGuard: CanActivateFn = async (
  _route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
): Promise<boolean | UrlTree> => {
  if (PUBLIC_ROUTE_PREFIXES.some((prefix) => state.url.startsWith(prefix))) {
    return true;
  }

  const router = inject(Router);
  const authService = inject(AuthService);
  const userContext = inject(UserContextService);
  const authorizationService = inject(AuthorizationService);
  const timersService = inject(TimersService);

  try {
    // 1. Verificar token válido
    if (authService.isTokenNoValid()) {
      return router.createUrlTree(['/auth'], {
        queryParams: { returnUrl: state.url },
      });
    }

    // 2. Recargar datos del usuario y permisos desde servidor.
    // En la primera carga después de un F5 (_dataLoadedFromServer = false) se usa el
    // mismo flujo que /auth (setUser) para garantizar token fresco + datos completos.
    // En navegaciones internas ya hay datos del servidor y basta con getUserData(true).
    try {
      if (!userContext.isDataLoadedFromServer()) {
        await authService.setUser();
        timersService.startTimerToken();
      } else {
        await userContext.getUserData(true);
      }
    } catch (error) {
      console.error('Error al cargar contexto del usuario:', error);
      return router.createUrlTree(['/auth'], {
        queryParams: { returnUrl: state.url },
      });
    }

    // 3. Recargar permisos después de actualizar el usuario
    try {
      await authorizationService.cargarPermisos(true); // force = true para recargar
    } catch (error) {
      console.error('Error al cargar permisos:', error);
      // Continuar sin permisos - el authorization guard manejará esto
    }

    return true;
  } catch (error) {
    console.error('Error inesperado en authGuard:', error);
    return router.createUrlTree(['/auth'], {
      queryParams: { returnUrl: state.url },
    });
  }
};

/**
 * Guard funcional para lazy loading de módulos.
 * Verifica que el usuario esté autenticado antes de cargar el módulo.
 */
export const canMatchGuard: CanMatchFn = async (
  _route: Route,
  segments: UrlSegment[],
): Promise<boolean | UrlTree> => {
  const currentPath = `/${segments.map((s) => s.path).join('/')}`;
  if (PUBLIC_ROUTE_PREFIXES.some((prefix) => currentPath.startsWith(prefix))) {
    return true;
  }

  const router = inject(Router);
  const authService = inject(AuthService);
  const userContext = inject(UserContextService);

  try {
    // Verificar token válido
    if (authService.isTokenNoValid()) {
      const url = segments.map((s) => s.path).join('/');
      return router.createUrlTree(['/auth'], {
        queryParams: { returnUrl: `/${url}` },
      });
    }

    // Verificar que el contexto esté cargado
    if (!userContext.isUserLoaded()) {
      try {
        await userContext.getUserData();
      } catch (error) {
        const url = segments.map((s) => s.path).join('/');
        return router.createUrlTree(['/auth'], {
          queryParams: { returnUrl: `/${url}` },
        });
      }
    }

    return true;
  } catch (error) {
    console.error('Error en canMatchGuard:', error);
    return false;
  }
};
